import { useCallback, useEffect } from 'react';

import { Amplify } from 'aws-amplify';
import {
  fetchAuthSession,
  fetchUserAttributes,
  signInWithRedirect,
  signOut as amplifySignOut,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

import awsConfig, { customProviderName } from 'src/config/aws-exports';
import { useAuth } from 'src/hooks';

import { AmplifyOutput } from './types';

// set initial config for amplify
Amplify.configure(awsConfig, {
  Storage: {
    S3: {
      prefixResolver: async () => '',
      defaultAccessLevel: 'guest',
    },
  },
});

const useAmplify = (): AmplifyOutput => {
  const { setAuth } = useAuth();

  useEffect(() => {
    // Debug
    Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          console.log('Amplify Sign In: ', payload.data);
          break;
        case 'signedOut':
          console.log('Amplify Sign Out');
          break;
        case 'customOAuthState':
          console.log('Amplify OAuth: ', payload.data);
          break;
        default:
          console.log('Amplify Other Events: ', payload);
          break;
      }
    });

    (async () => {
      try {
        const [attributes, session] = await Promise.all([
          fetchUserAttributes(),
          fetchAuthSession(),
        ]);

        const email = attributes.email ?? '';
        const familyName = attributes.family_name ?? '';
        const givenName = attributes.given_name ?? '';
        const phoneNumber = attributes.phone_number ?? '';
        const id = attributes['custom:ciq_userid'] ?? '';
        const cognitoToken = session.tokens?.idToken?.toString() ?? '';
        const cognitoGroups = (session.tokens?.idToken?.payload?.[
          'cognito:groups'
        ] ?? []) as string[];

        console.log('current authenticated user attributes: ', attributes);

        setAuth({
          isAuthenticated: true,
          user: {
            email,
            familyName,
            givenName,
            phoneNumber,
            cognitoGroups,
            cognitoToken,
            id,
          },
        });
      } catch (error) {
        console.log('Get Current Authenticated User Failed:', error);
        setAuth({
          isAuthenticated: false,
          user: null,
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = useCallback(() => {
    if (customProviderName) {
      signInWithRedirect({ provider: { custom: customProviderName } });
    }
  }, []);

  const signOut = useCallback(() => {
    amplifySignOut();
  }, []);

  return {
    awsConfig,
    signIn,
    signOut,
  };
};

export default useAmplify;
